import { useState, useEffect, useMemo } from 'react';
import { Combobox, type ComboboxOption } from '../ui/Combobox';
import { getLocationService } from '../../services/locationServiceInstance';
import type { Country } from '../../types/location';

export interface CountrySelectorProps {
  readonly value?: Pick<Country, 'id' | 'name' | 'iso2'> | null;
  readonly onChange: (country: Country | null) => void;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly label?: string;
  readonly placeholder?: string;
  readonly error?: string;
  readonly className?: string;
}

/**
 * CountrySelector Component
 * 
 * Dropdown component for selecting a country with search/filter capabilities
 * 
 * Features:
 * - Loading state while fetching countries
 * - Search/filter functionality with debouncing
 * - Alphabetical sorting
 * - Error state handling
 * - Accessibility (ARIA labels, keyboard navigation)
 * - Responsive design
 * - Uses app's Combobox component for consistent UI
 */
export function CountrySelector({
  value,
  onChange,
  disabled = false,
  required = false,
  label = 'País',
  placeholder = 'Seleccione un país',
  error,
  className = ''
}: CountrySelectorProps) {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState<string | null>(null);

  // Load countries on mount
  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    setLoading(true);
    setErrorState(null);
    
    try {
      const service = getLocationService();
      const data = await service.getCountries();
      setCountries(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al cargar países';
      setErrorState(message);
    } finally {
      setLoading(false);
    }
  };

  const regionLabels: Record<string, string> = {
    'Africa': 'África',
    'Americas': 'Américas',
    'Asia': 'Asia',
    'Europe': 'Europa',
    'Oceania': 'Oceanía',
    'Polar': 'Polar',
  };

  const subregionLabels: Record<string, string> = {
    'Northern Africa': 'Norte de África',
    'Western Africa': 'África Occidental',
    'Middle Africa': 'África Central',
    'Eastern Africa': 'África Oriental',
    'Southern Africa': 'África Austral',
    'Northern America': 'Norteamérica',
    'Central America': 'Centroamérica',
    'South America': 'Sudamérica',
    'Caribbean': 'Caribe',
    'Western Europe': 'Europa Occidental',
    'Eastern Europe': 'Europa del Este',
    'Northern Europe': 'Europa del Norte',
    'Southern Europe': 'Europa del Sur',
    'Central Asia': 'Asia Central',
    'Eastern Asia': 'Asia Oriental',
    'Southern Asia': 'Asia del Sur',
    'South-Eastern Asia': 'Sudeste Asiático',
    'Western Asia': 'Asia Occidental',
    'Australia and New Zealand': 'Australia y Nueva Zelanda',
    'Melanesia': 'Melanesia',
    'Micronesia': 'Micronesia',
    'Polynesia': 'Polinesia',
  };

  // Convert countries to Combobox options
  const options: ComboboxOption[] = useMemo(() => {
    return countries
      .slice().sort((a, b) => (a.nombre_es || a.name).localeCompare(b.nombre_es || b.name, 'es'))
      .map(country => {
        const sub = subregionLabels[country.subregion] || country.subregion;
        const reg = regionLabels[country.region] || country.region;
        const geo = sub ? `${sub}, ${reg}` : reg;
        return {
          value: country.id,
          label: country.nombre_es || country.name,
          subtitle: `${country.iso2} · ${geo}`
        };
      });
  }, [countries]);

  const handleChange = (selectedId: string) => {
    if (!selectedId) {
      onChange(null);
      return;
    }

    const country = countries.find(c => c.id === selectedId);
    if (country) {
      onChange(country);
    }
  };

  // Resolve the Combobox value: prefer id, fallback to iso2 lookup
  const resolvedValue = useMemo(() => {
    if (!value) return '';
    if (value.id) return value.id;
    // Fallback: find country by iso2 (e.g. when editing with only codigo_iso)
    const match = countries.find(c => c.iso2 === value.iso2);
    return match?.id || '';
  }, [value, countries]);

  const displayError = error || errorState;
  const displayPlaceholder = loading ? 'Cargando países...' : placeholder;

  return (
    <div className={className}>
      <Combobox
        options={options}
        value={resolvedValue}
        onChange={handleChange}
        label={label}
        placeholder={displayPlaceholder}
        required={required}
        disabled={disabled || loading}
        error={displayError || undefined}
        emptyMessage="No se encontraron países"
        allowClear={true}
      />
    </div>
  );
}
